
import { memo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BellOff, PauseCircle, Timer } from 'lucide-react';
import type { Ticket } from '@/entities/ticket';
import { slaChipLabel, slaChipTitle, slaTone } from '@/entities/ticket';
import { TicketContextMenu } from '@/features/tickets/ui/TicketContextMenu';
import { useAuthStore } from '@/features/auth';
import { useMassIncidentUi } from '@/features/mass-incidents';
import {
  CriticalPriorityIcon,
  EmailIcon,
  MassIncidentIcon,
  TICKET_PRIORITY_LABELS,
  VipIcon,
  TERMINAL_TICKET_STATUSES,
  type TicketPriority,
} from '@/shared/config';
import { Avatar, StatusBadge, Tooltip } from '@/shared/ui';
import { cn } from '@/shared/lib/cn';
import { useLongPress } from '@/shared/lib/useLongPress';
import styles from './TicketCard.module.css';

export interface TicketCardProps {
  ticket: Ticket;
  showTakeButton?: boolean;
  showDetails?: boolean;
  currentUserId?: string;
  onTake?: (id: string) => void;
  onSelect?: (id: string) => void;
  active?: boolean;
  fromPath?: string;
}

const SLA_TONE_CLASS: Record<string, string | undefined> = {
  ok: undefined,
  warning: styles.slaWarning,
  burning: styles.slaBurning,
  breached: styles.slaBreachedTone,
  paused: styles.slaPaused,
};

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('ru', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return `${d.toLocaleDateString('ru', { day: '2-digit', month: '2-digit', year: 'numeric' })} ${d.toLocaleTimeString('ru', { hour: '2-digit', minute: '2-digit' })}`;
}

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;
function isFresh(iso: string): boolean {
  return Date.now() - new Date(iso).getTime() < WEEK_MS;
}

function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60000);
  if (min < 1) return 'только что';
  if (min < 60) return `${min} мин назад`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} ч назад`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d} дн назад`;
  return formatDate(iso);
}

const TicketCardBase: React.FC<TicketCardProps> = ({
  ticket,
  showTakeButton = false,
  showDetails = false,
  currentUserId = '',
  onTake,
  onSelect,
  active = false,
  fromPath,
}) => {
  const canChangeStatus = useAuthStore((s) => s.hasPermission('change_status'));
  const openIncidentPanel = useMassIncidentUi((s) => s.openPanel);
  const assignees = ticket.assignees ?? [];
  const isAssignedToMe = currentUserId && assignees.some((a) => a.userId === currentUserId);
  const isOwnTicket = Boolean(currentUserId) && ticket.creatorId === currentUserId;
  const priority = ticket.priority as TicketPriority;
  const showPriority = showDetails;
  const showSla = showDetails;
  const isTerminal = TERMINAL_TICKET_STATUSES.includes(ticket.status);
  const assigneeUsers = assignees
    .map((a) => a.user)
    .filter((u): u is NonNullable<typeof u> => Boolean(u?.displayName));

  const canOpenIncident = showDetails && canChangeStatus && !onSelect;
  const handleOpenIncident = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (ticket.massIncident) openIncidentPanel(ticket.massIncident.id);
  };

  const handleTake = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onTake?.(ticket.id);
  };

  const meta = (
    <div className={styles.metaRow}>
      { }
      {showPriority && (
        <Tooltip content={`Приоритет: ${TICKET_PRIORITY_LABELS[priority].toLowerCase()}`}>
          <span
            className={cn(styles.priority, styles[`priority-${priority}`])}
            role="img"
            aria-label={`Приоритет: ${TICKET_PRIORITY_LABELS[priority]}`}
          >
            {priority === 'critical' ? (
              <CriticalPriorityIcon size={15} strokeWidth={2.25} className={styles.flame} aria-hidden />
            ) : (
              <>
                <span className={styles.bar} />
                <span className={styles.bar} />
                <span className={styles.bar} />
              </>
            )}
          </span>
        </Tooltip>
      )}
      {ticket.number != null && <span className={styles.number}>№{ticket.number}</span>}
      {showDetails && ticket.requesterVip && (
        <Tooltip content="VIP-заявитель: открытые заявки стоят первыми">
          <span className={styles.vip}>
            <VipIcon size={12} aria-hidden />
            VIP
          </span>
        </Tooltip>
      )}
      {ticket.channel === 'email' && (
        <Tooltip content="Заявка пришла письмом">
          <span className={styles.channel} role="img" aria-label="Заявка из почты">
            <EmailIcon size={13} aria-hidden />
          </span>
        </Tooltip>
      )}
      <div className={styles.divider}></div>
      { }
      <span
        title={
          showDetails
            ? `Создана ${formatDateTime(ticket.createdAt)}, обновлена ${timeAgo(ticket.updatedAt)}`
            : formatDateTime(ticket.createdAt)
        }
      >
        {showDetails && isFresh(ticket.createdAt)
          ? timeAgo(ticket.createdAt)
          : formatDate(ticket.createdAt)}
      </span>
      {isOwnTicket && showDetails && <span className={styles.ownTicket}>ваша заявка</span>}
      {isAssignedToMe && showDetails && <span className={styles.assignedToMe}>в работе у вас</span>}
      {ticket.muted && (
        <span
          className={styles.muted}
          title="Уведомления по заявке приглушены: приходят молча, без звука, всплывашек и писем"
        >
          <BellOff size={11} aria-hidden />
          <span className={styles.srOnly}>Уведомления приглушены</span>
        </span>
      )}
      {ticket.massIncident &&
        (canOpenIncident ? (
          <button
            type="button"
            className={cn(
              styles.incident,
              styles.incidentButton,
              ticket.massIncident.status === 'resolved' && styles.incidentResolved,
            )}
            title={`Массовый сбой: ${ticket.massIncident.title} — открыть состав`}
            onClick={handleOpenIncident}
          >
            <MassIncidentIcon size={12} aria-hidden />
            массовый сбой
          </button>
        ) : (
          <span
            className={cn(
              styles.incident,
              ticket.massIncident.status === 'resolved' && styles.incidentResolved,
            )}
            title={`Массовый сбой: ${ticket.massIncident.title}`}
          >
            <MassIncidentIcon size={12} aria-hidden />
            массовый сбой
          </span>
        ))}
    </div>
  );

  const chips = (
    <div className={styles.chips}>
      <StatusBadge status={ticket.status} label={ticket.statusLabel} />
      { }
      {showSla &&
        (ticket.sla ? (
          <span
            className={cn(styles.badge, styles.sla, SLA_TONE_CLASS[slaTone(ticket.sla)])}
            title={slaChipTitle(ticket.sla)}
          >
            {ticket.sla.paused ? <PauseCircle size={12} aria-hidden /> : <Timer size={12} aria-hidden />}
            {slaChipLabel(ticket.sla)}
          </span>
        ) : (
          ticket.slaResolutionBreached && (
            <span
              className={cn(styles.badge, styles.slaBreached)}
              title="Срок решения нарушен (считается по рабочим минутам календаря группы)"
            >
              Просрочено
            </span>
          )
        ))}
      {ticket.assignedGroup && !showDetails && (
        <span className={styles.workgroupChip} title={ticket.assignedGroup.name}>
          {ticket.assignedGroup.name}
        </span>
      )}
    </div>
  );

  const title = (
    <span className={styles.topic}>
      {(ticket as { subject?: string; title: string }).subject ?? ticket.title}
    </span>
  );

  const takeButton = showTakeButton && assignees.length === 0 && !isTerminal && (
    <button type="button" className={styles.take} onClick={handleTake}>
      Взять в работу
    </button>
  );
  const footerAction = takeButton;

  const hasAssignees = assigneeUsers.length > 0;
  const creatorAvatar = ticket.creator?.displayName && (
    <Tooltip
      content={`Заявитель: ${isOwnTicket ? 'вы' : ticket.creator.displayName}${
        !isOwnTicket && ticket.creator.department ? ` · ${ticket.creator.department}` : ''
      }`}
    >
      <Avatar
        className={styles.avatar}
        name={ticket.creator.displayName}
        seed={ticket.creator.id}
        src={ticket.creator.avatarUrl}
        aria-label={`Заявитель: ${ticket.creator.displayName}`}
      />
    </Tooltip>
  );
  const people = showDetails && (
    <span className={styles.people}>
      {creatorAvatar}
      {hasAssignees ? (
        <>
          <ArrowRight size={12} className={styles.peopleArrow} aria-hidden />
          {assigneeUsers.slice(0, 3).map((u) => (
            <Tooltip key={u.id} content={`Исполнитель: ${u.id === currentUserId ? 'вы' : u.displayName}`}>
              <Avatar
                className={styles.avatar}
                name={u.displayName}
                seed={u.id}
                src={u.avatarUrl}
                aria-label={`Исполнитель: ${u.displayName}`}
              />
            </Tooltip>
          ))}
          {assigneeUsers.length > 3 && (
            <Tooltip content={assigneeUsers.slice(3).map((u) => u.displayName).join('\n')}>
              <span className={cn(styles.avatar, styles.avatarMore)}>+{assigneeUsers.length - 3}</span>
            </Tooltip>
          )}
        </>
      ) : (
        ticket.creator?.displayName && (
          <span className={styles.personName}>{isOwnTicket ? 'вы' : ticket.creator.displayName}</span>
        )
      )}
    </span>
  );
  const unassignedNote = showDetails && assignees.length === 0 && !takeButton && !isTerminal && (
    <span className={styles.unassigned}>без исполнителя</span>
  );

  const inner = showDetails ? (
    <div className={styles.body}>
      <div className={styles.headerRow}>
        {meta}
        {chips}
      </div>
      {title}
      <div className={styles.footerRow}>
        <div className={styles.details}>
          {people}
          {unassignedNote}
          {ticket.assignedGroup && (
            <span className={styles.group} title={ticket.assignedGroup.name}>
              {ticket.assignedGroup.name}
            </span>
          )}
        </div>
        {footerAction && <div className={styles.actions}>{footerAction}</div>}
      </div>
    </div>
  ) : (
    <div className={styles.itemRow}>
      <div className={styles.itemContent}>
        {meta}
        {title}
        {chips}
      </div>
    </div>
  );

  const needsReply =
    !showDetails &&
    (ticket.status === 'waiting_for_requester' || Boolean(ticket.awaitsRequesterDecision));
  const needsApproval = !showDetails && ticket.needsMyApproval === true;
  const className = cn(
    styles.card,
    isOwnTicket && showDetails && styles.cardOwn,
    isAssignedToMe && showDetails && styles.cardAssigned,
    showDetails && ticket.requesterVip && !isTerminal && styles.cardVip,
    active && styles.cardActive,
    needsReply && styles.cardPulse,
    needsApproval && styles.cardPulseApproval,
  );

  const { pressing, handlers } = useLongPress();
  const pressProps = { ...handlers, 'data-pressing': pressing || undefined };

  const card = onSelect ? (
    <button
      type="button"
      className={className}
      data-status={ticket.status}
      aria-pressed={active}
      onClick={() => onSelect(ticket.id)}
      {...pressProps}
    >
      {inner}
    </button>
  ) : (
    <Link
      className={className}
      to={`/tickets/view/${ticket.id}`}
      state={fromPath ? { from: fromPath } : undefined}
      data-status={ticket.status}
      {...pressProps}
    >
      {inner}
    </Link>
  );

  return (
    <TicketContextMenu ticket={ticket} currentUserId={currentUserId} onTake={onTake}>
      {card}
    </TicketContextMenu>
  );
};

export const TicketCard = memo(TicketCardBase);
